import { useRef, useState } from 'react'
import { usePresignUpload, type UploadContext } from '../../hooks/useChurchAdmin'

interface ImageUploadProps {
  context: UploadContext
  onUpload: (publicUrl: string) => void
  children: React.ReactNode
  className?: string
  maxSizeMb?: number
}

const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp'] as const
const ACCEPT_ATTR = ACCEPTED.join(',')

const MAX_DIM: Record<UploadContext, number> = {
  'event-image': 1600,
  'church-cover': 1600,
  'church-logo': 512,
}

async function resizeImage(file: File, maxDim: number): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(url)
      const { naturalWidth: w, naturalHeight: h } = img
      if (w <= maxDim && h <= maxDim) {
        resolve(file)
        return
      }
      const scale = maxDim / Math.max(w, h)
      const canvas = document.createElement('canvas')
      canvas.width = Math.round(w * scale)
      canvas.height = Math.round(h * scale)
      const ctx = canvas.getContext('2d')!
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      canvas.toBlob(
        (blob) => {
          if (!blob) { reject(new Error('Resize failed')); return }
          const name = file.name.replace(/\.[^.]+$/, '.webp')
          resolve(new File([blob], name, { type: 'image/webp' }))
        },
        'image/webp',
        0.88,
      )
    }
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not load image')) }
    img.src = url
  })
}

export function ImageUpload({
  context, onUpload, children, className, maxSizeMb = 5,
}: ImageUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const presign = usePresignUpload()
  const [error, setError] = useState<string | null>(null)
  const [isResizing, setIsResizing] = useState(false)

  const isPending = presign.isPending || isResizing

  async function handleFile(file: File) {
    setError(null)

    if (!ACCEPTED.includes(file.type as typeof ACCEPTED[number])) {
      setError('Only JPEG, PNG, or WebP images are allowed.')
      return
    }
    if (file.size > 20 * 1024 * 1024) {
      setError('Image must be under 20 MB.')
      return
    }

    let ready = file
    try {
      setIsResizing(true)
      ready = await resizeImage(file, MAX_DIM[context])
    } catch {
      setError('Could not process image. Please try a different file.')
      setIsResizing(false)
      return
    } finally {
      setIsResizing(false)
    }

    if (ready.size > maxSizeMb * 1024 * 1024) {
      setError(`Image must be under ${maxSizeMb} MB after resizing.`)
      return
    }

    try {
      const { data } = await presign.mutateAsync({ context, contentType: ready.type })
      await fetch(data.uploadUrl, {
        method: 'PUT',
        body: ready,
        headers: { 'Content-Type': ready.type },
      })
      onUpload(data.publicUrl)
    } catch {
      setError('Upload failed. Please try again.')
    }
  }

  return (
    <div className={className}>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_ATTR}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) void handleFile(file)
          e.target.value = ''
        }}
      />
      <div
        onClick={() => !isPending && inputRef.current?.click()}
        style={{ cursor: isPending ? 'wait' : 'pointer', opacity: isResizing ? 0.6 : 1 }}
      >
        {children}
      </div>
      {error && (
        <p className="mt-1.5 text-[11.5px] text-warn">{error}</p>
      )}
    </div>
  )
}
